import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useAuth } from '../contexts/AuthContext';
import Layout from '../components/Layout';
import { 
  FileText, 
  X, 
  Send,
  CheckCircle,
  Clock,
  AlertCircle,
  Eye,
  RefreshCw,
  Play,
  Globe,
  Settings,
  Zap
} from 'lucide-react';
import api from '../services/api';
import toast from 'react-hot-toast';
import CasaFacilReportButtons from '../components/CasaFacilReportButtons';
import { useCasaFacilProduct, casaFacilProductFromName } from '../lib/casaFacilProduct';
import { groupByReferenceMonth } from '../lib/referencePeriod';

/**
 * Decodes a JSON string, or returns a value the API already parsed.
 */
function parseJsonValue(value) {
  if (value == null || value === '') return null;
  if (typeof value === 'string') {
    try {
      return JSON.parse(value);
    } catch {
      return value;
    }
  }
  return value;
}

/**
 * Reads one critique from MAXPAR whether it is a string or an object.
 */
function critiqueText(item) {
  if (item == null) return '';
  if (typeof item === 'string') return item;
  if (typeof item === 'object') {
    return item.Mensagem || item.message || item.Descricao || item.descricao || JSON.stringify(item);
  }
  return String(item);
}

/**
 * Flattens the stored integration payload into one row per policy.
 */
function getIntegrationRows(send) {
  const response = parseJsonValue(send?.api_response);
  const batch = response && Array.isArray(response.batch_results) ? response.batch_results : [];

  return batch.map((item, index) => {
    const maxpar = item?.result?.data || {};
    const critiques = Array.isArray(maxpar.Criticas) ? maxpar.Criticas.map(critiqueText).filter(Boolean) : [];
    const created = Boolean(maxpar.ApoliceCriada);
    const updated = Boolean(maxpar.ApoliceAtualizada);
    const success = Boolean(item?.success) && item?.result?.success !== false && maxpar.Sucesso !== false;

    return {
      key: `${item?.row ?? index}-${item?.sent_payload?.Apolice || index}`,
      policy: item?.sent_payload?.Apolice || item?.data?.external_id || '—',
      name: item?.sent_payload?.Segurado?.Nome || item?.data?.customer_name || '',
      success,
      created,
      updated,
      error: item?.error || item?.result?.error || '',
      critiques
    };
  });
}

/**
 * Right-hand panel with the outcome of one API send.
 */
function SendDetailsPanel({ send, loading, error, onClose, getStatusText, formatDateTime }) {
  const rows = send ? getIntegrationRows(send) : [];
  const okCount = rows.filter((row) => row.success).length;
  const failCount = rows.length - okCount;
  const response = parseJsonValue(send?.api_response);
  const responseText = response == null
    ? ''
    : (typeof response === 'string' ? response : JSON.stringify(response, null, 2));

  return createPortal(
    <div className="fixed inset-0 z-[80] flex justify-end">
      <button
        type="button"
        className="absolute inset-0 bg-[#231f20]/40"
        aria-label="Fechar resultado da integração"
        onClick={onClose}
      />
      <aside
        role="dialog"
        aria-modal="true"
        aria-label="Resultado da integração"
        className="relative flex h-full w-full max-w-lg flex-col bg-white shadow-2xl"
      >
        <div className="flex items-start justify-between gap-4 border-b border-gray-200 px-5 py-4">
          <div className="min-w-0">
            <h3 className="text-lg font-semibold text-[#231f20]">Resultado da integração</h3>
            <p className="mt-1 truncate text-sm text-gray-600">
              {send?.file_original_name || 'Carregando envio...'}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-700"
            aria-label="Fechar"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-4">
          {loading && (
            <div className="flex items-center justify-center py-16">
              <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-primary-600" />
            </div>
          )}

          {!loading && error && (
            <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-800">
              {error}
            </div>
          )}

          {!loading && send && (
            <>
              <div className="mb-4 grid grid-cols-2 gap-3 text-sm">
                <div>
                  <span className="font-medium text-gray-500">Status</span>
                  <p className="text-gray-900">{getStatusText(send.status)}</p>
                </div>
                <div>
                  <span className="font-medium text-gray-500">HTTP</span>
                  <p className="text-gray-900">{send.api_status_code || '—'}</p>
                </div>
                <div>
                  <span className="font-medium text-gray-500">Enviado em</span>
                  <p className="text-gray-900">{formatDateTime(send.sent_at) || 'Não enviado'}</p>
                </div>
                <div>
                  <span className="font-medium text-gray-500">API</span>
                  <p className="text-gray-900">{send.api_owner || '—'}</p>
                  <p className="text-xs text-gray-500">{send.api_function}</p>
                </div>
              </div>

              {send.endpoint && (
                <p className="mb-4 break-all font-mono text-xs text-gray-500">{send.method} {send.endpoint}</p>
              )}

              {rows.length > 0 && (
                <div className="mb-4 grid grid-cols-3 gap-2 text-center">
                  <div className="rounded-lg bg-gray-50 px-2 py-3">
                    <div className="text-lg font-semibold text-gray-900">{rows.length}</div>
                    <div className="text-xs text-gray-500">Registros</div>
                  </div>
                  <div className="rounded-lg bg-green-50 px-2 py-3">
                    <div className="text-lg font-semibold text-green-700">{okCount}</div>
                    <div className="text-xs text-green-600">Sucesso</div>
                  </div>
                  <div className="rounded-lg bg-red-50 px-2 py-3">
                    <div className="text-lg font-semibold text-red-700">{failCount}</div>
                    <div className="text-xs text-red-600">Falhas</div>
                  </div>
                </div>
              )}

              {send.api_error_message && (
                <div className="mb-4 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-800">
                  {send.api_error_message}
                </div>
              )}

              {rows.length > 0 ? (
                <ul className="divide-y divide-gray-100 rounded-lg border border-gray-200">
                  {rows.map((row) => (
                    <li key={row.key} className="px-3 py-3">
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <p className="font-mono text-sm text-gray-900">{row.policy}</p>
                          {row.name && <p className="truncate text-sm text-gray-600">{row.name}</p>}
                        </div>
                        <span className={`shrink-0 rounded-full px-2 py-0.5 text-xs font-medium ${
                          row.success ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                        }`}>
                          {row.success ? (row.created ? 'Criada' : row.updated ? 'Atualizada' : 'Aceita') : 'Falhou'}
                        </span>
                      </div>
                      {row.error && <p className="mt-1 text-xs text-red-700">{row.error}</p>}
                      {row.critiques.length > 0 && (
                        <ul className="mt-1 list-disc pl-4 text-xs text-amber-800">
                          {row.critiques.map((critique, index) => (
                            <li key={`${row.key}-critique-${index}`}>{critique}</li>
                          ))}
                        </ul>
                      )}
                    </li>
                  ))}
                </ul>
              ) : responseText ? (
                <pre className="overflow-x-auto rounded-lg border border-gray-200 bg-gray-50 p-3 text-xs text-gray-800 whitespace-pre-wrap">
                  {responseText}
                </pre>
              ) : (
                <p className="text-sm text-gray-500">Este envio ainda não tem resposta da integração.</p>
              )}
            </>
          )}
        </div>
      </aside>
    </div>,
    document.body
  );
}

function ApiSends() {
  const { user } = useAuth();
  const product = useCasaFacilProduct();
  const [apiSends, setApiSends] = useState([]);
  const [processedFiles, setProcessedFiles] = useState([]);
  const [apiConfigs, setApiConfigs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedSendData, setSelectedSendData] = useState(null);
  const [viewingData, setViewingData] = useState(false);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [detailsError, setDetailsError] = useState('');
  const [showIntegrationModal, setShowIntegrationModal] = useState(false);
  const [resendMode, setResendMode] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [selectedApiConfig, setSelectedApiConfig] = useState('');
  const [integrationLoading, setIntegrationLoading] = useState(false);
  const [showJsonPreview, setShowJsonPreview] = useState(false);
  const [jsonPreviewData, setJsonPreviewData] = useState(null);

  console.log('🔄 Componente ApiSends renderizando...', { user });

  // Carregar dados
  useEffect(() => {
    console.log('📥 useEffect executando...');
    loadData();
  }, []);

  useEffect(() => {
    if (!viewingData) return undefined;
    const onKeyDown = (event) => {
      if (event.key === 'Escape') {
        setViewingData(false);
        setSelectedSendData(null);
        setDetailsError('');
        setDetailsLoading(false);
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [viewingData]);

  /**
   * Loads sends first so a file already integrated is marked before the list renders.
   */
  const loadData = async () => {
    const sends = await loadApiSends();
    await Promise.all([
      loadProcessedFiles(sends),
      loadApiConfigs()
    ]);
  };

  /**
   * Returns the integration history and keeps it in state.
   */
  const loadApiSends = async () => {
    try {
      console.log('📡 Carregando envios API...');
      const response = await api.get('/upload/api-sends');
      console.log('✅ Envios API carregados:', response.data);
      const sends = response.data.api_sends || [];
      setApiSends(sends);
      return sends;
    } catch (error) {
      console.error('❌ Erro ao carregar envios API:', error);
      return [];
    }
  };

  /**
   * Lists processed files and marks those already sent so Integrar stays disabled.
   */
  const loadProcessedFiles = async (sends = []) => {
    try {
      console.log('📁 Carregando arquivos processados...');
      const response = await api.get('/upload/files');
      console.log('✅ Arquivos carregados:', response.data);

      const completedFiles = response.data.files?.filter(file =>
        file.status === 'completed' || file.status === 'processed'
      ) || [];

      const integratedFileIds = new Set(
        sends
          .filter(send => send.status === 'sent')
          .map(send => String(send.file_upload_id))
      );

      const availableFiles = completedFiles.map(file => ({
        ...file,
        alreadyIntegrated: integratedFileIds.has(String(file.id))
      }));

      console.log(`📁 Arquivos processados: ${completedFiles.length}`);
      console.log(`📁 Já integrados: ${integratedFileIds.size}`);

      setProcessedFiles(availableFiles);
    } catch (error) {
      console.error('❌ Erro ao carregar arquivos:', error);
    }
  };

  const loadApiConfigs = async () => {
    try {
      console.log('⚙️ Carregando configurações de API...');
      const response = await api.get('/api-configs/active/list');
      console.log('✅ Configurações de API carregadas:', response.data);
      setApiConfigs(response.data.configs || []);
    } catch (error) {
      console.error('❌ Erro ao carregar configurações de API:', error);
    } finally {
      setLoading(false);
    }
  };

  const sendToApi = async (fileId) => {
    if (!window.confirm('Tem certeza que deseja enviar este arquivo para integração?')) {
      return;
    }

    try {
      console.log('🚀 Enviando arquivo para API:', fileId);
      const response = await api.post(`/upload/api-sends/${fileId}/send`);
      console.log('✅ Resposta da API:', response.data);
      
      await loadApiSends();
      alert('Arquivo enviado para integração com sucesso!');
    } catch (error) {
      console.error('❌ Erro ao enviar para API:', error);
      alert('Erro ao enviar arquivo para integração');
    }
  };

  const retrySend = async (sendId) => {
    if (!window.confirm('Tem certeza que deseja tentar novamente este envio?')) {
      return;
    }

    try {
      console.log('🔄 Tentando novamente envio:', sendId);
      const response = await api.post(`/upload/api-sends/${sendId}/retry`);
      console.log('✅ Resposta da API:', response.data);
      
      await loadApiSends();
      alert('Tentativa de envio iniciada!');
    } catch (error) {
      console.error('❌ Erro ao tentar novamente:', error);
      alert('Erro ao tentar envio novamente');
    }
  };

  const viewSendDetails = async (sendId) => {
    setViewingData(true);
    setSelectedSendData(null);
    setDetailsError('');
    setDetailsLoading(true);
    try {
      const response = await api.get(`/upload/api-sends/${sendId}/details`);
      setSelectedSendData(response.data);
    } catch (error) {
      console.error('Erro ao carregar detalhes do envio:', error);
      setDetailsError(error.response?.data?.error || 'Erro ao carregar detalhes do envio');
    } finally {
      setDetailsLoading(false);
    }
  };

  const closeDataView = () => {
    setViewingData(false);
    setSelectedSendData(null);
    setDetailsError('');
    setDetailsLoading(false);
  };

  /**
   * Opens the send dialog. A file already integrated can only be opened as a resend.
   */
  const openIntegrationModal = (file, isUpdate = false) => {
    if (file?.alreadyIntegrated && !isUpdate) return;
    setSelectedFile(file);
    setSelectedApiConfig('');
    setResendMode(Boolean(isUpdate));
    setShowIntegrationModal(true);
    setIntegrationLoading(false);
    
    // Se for atualização, buscar a configuração atual
    if (isUpdate && file) {
      const existingSend = apiSends.find(s => s.file_upload_id === file.id);
      if (existingSend) {
        setSelectedApiConfig(existingSend.api_config_id);
      }
    }
  };

  const closeIntegrationModal = () => {
    setShowIntegrationModal(false);
    setSelectedFile(null);
    setSelectedApiConfig('');
    setResendMode(false);
    setShowJsonPreview(false);
    setJsonPreviewData(null);
  };

  const handleIntegration = async () => {
    console.log('🚀 Iniciando integração...');
    console.log('📁 Arquivo selecionado:', selectedFile);
    console.log('⚙️ Configuração selecionada:', selectedApiConfig);
    
    if (!selectedApiConfig) {
      toast.error('Selecione uma configuração de API');
      return;
    }

    if (!selectedFile || !selectedFile.id) {
      console.error('❌ Arquivo não selecionado:', selectedFile);
      toast.error('Arquivo não selecionado');
      return;
    }

    if (!selectedApiConfig || selectedApiConfig === '') {
      console.error('❌ Configuração de API não selecionada:', selectedApiConfig);
      toast.error('Configuração de API não selecionada');
      return;
    }

    console.log('✅ Validações passaram - arquivo e configuração selecionados');

    const isUpdate = resendMode;
    const actionText = isUpdate ? 'atualizar' : 'integrar';
    
    console.log('🔄 Tipo de ação:', actionText);
    console.log('📊 Status atual:', apiSends.find(s => s.file_upload_id === selectedFile?.id)?.status);
    
    if (!window.confirm(`Tem certeza que deseja ${actionText} o arquivo "${selectedFile.original_name}" com a API selecionada?`)) {
      return;
    }

    try {
      setIntegrationLoading(true);
      
      const requestData = {
        api_config_id: selectedApiConfig,
        is_update: isUpdate
      };
      
      console.log('📤 Dados da requisição:', requestData);
      console.log('🌐 URL da requisição:', `/upload/api-sends/${selectedFile.id}/send`);
      
      // Enviar para integração (criação ou atualização)
      console.log('📡 Fazendo requisição POST...');
      console.log('🔍 Verificando variáveis antes da requisição:');
      console.log('  - selectedFile:', selectedFile);
      console.log('  - selectedFile.id:', selectedFile?.id);
      console.log('  - selectedApiConfig:', selectedApiConfig);
      console.log('  - requestData:', requestData);
      
      let response;
      try {
        response = await api.post(`/upload/api-sends/${selectedFile.id}/send`, requestData);
        console.log('✅ Resposta recebida:', response);
      } catch (apiError) {
        console.error('❌ Erro específico da API:', apiError);
        throw apiError;
      }

      if (response.data.success || response.data.message) {
        const successMessage = isUpdate ? 'Integração atualizada com sucesso!' : 'Integração iniciada com sucesso!';
        toast.success(successMessage);
        closeIntegrationModal();
        
        try {
          console.log('🔄 Recarregando dados...');
          await loadData(); // Recarregar dados
          console.log('✅ Dados recarregados com sucesso');
        } catch (loadError) {
          console.error('❌ Erro ao recarregar dados:', loadError);
          // Não falhar a integração por causa do erro no reload
        }
      } else {
        toast.error(response.data.error || `Erro ao ${actionText} integração`);
      }
    } catch (error) {
      console.error(`❌ Erro na ${actionText}:`, error);
      console.error('❌ Detalhes do erro:', {
        message: error.message,
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
        config: {
          url: error.config?.url,
          method: error.config?.method,
          data: error.config?.data
        }
      });
      toast.error(`Erro ao ${actionText} integração: ${error.message}`);
    } finally {
      setIntegrationLoading(false);
    }
  };

  const handlePreviewJson = async () => {
    if (!selectedApiConfig) {
      toast.error('Selecione uma configuração de API');
      return;
    }

    try {
      setIntegrationLoading(true);
      
      // Usar nova rota que formata os dados usando o serviço MAXPAR
      const response = await api.get(`/upload/files/${selectedFile.id}/json-preview/${selectedApiConfig}`);
      
      if (response.data.success && response.data.preview) {
        setJsonPreviewData(response.data.preview);
        setShowJsonPreview(true);
      } else {
        toast.error('Não foi possível gerar o preview formatado do JSON');
      }
    } catch (error) {
      console.error('❌ Erro ao gerar preview:', error);
      toast.error('Erro ao gerar preview do JSON');
    } finally {
      setIntegrationLoading(false);
    }
  };

  const closeJsonPreview = () => {
    setShowJsonPreview(false);
    setJsonPreviewData(null);
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'pending':
        return <Clock className="h-4 w-4 text-primary-500" />;
      case 'sent':
        return <CheckCircle className="h-4 w-4 text-green-500" />;
      case 'failed':
        return <AlertCircle className="h-4 w-4 text-red-500" />;
      case 'cancelled':
        return <X className="h-4 w-4 text-gray-500" />;
      default:
        return <FileText className="h-4 w-4 text-gray-500" />;
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'pending':
        return 'Pendente';
      case 'sent':
        return 'Enviado';
      case 'failed':
        return 'Falhou';
      case 'cancelled':
        return 'Cancelado';
      default:
        return 'Desconhecido';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending':
        return 'text-primary-600 bg-primary-50';
      case 'sent':
        return 'text-green-600 bg-green-50';
      case 'failed':
        return 'text-red-600 bg-red-50';
      case 'cancelled':
        return 'text-gray-600 bg-gray-50';
      default:
        return 'text-gray-600 bg-gray-50';
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('pt-BR');
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleString('pt-BR');
  };

  const matchesProduct = (name) => !product || casaFacilProductFromName(name)?.id === product.id;
  const visibleProcessedFiles = processedFiles.filter((file) => matchesProduct(file.original_name));
  const visibleApiSends = apiSends.filter((send) => matchesProduct(send.file_original_name));

  console.log('🎨 Renderizando JSX...');
  
  return (
    <Layout 
      headerTitle={product?.label || 'CASA FÁCIL RESOLVE'}
      headerSubtitle="Envios para a MAXPAR"
    >
      {/* Estatísticas */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-6 mb-8">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="text-center">
            <div className="text-2xl font-bold text-green-600">
              {visibleProcessedFiles.length}
            </div>
            <div className="text-sm text-green-500">Arquivos Processados</div>
          </div>
        </div>
        
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="text-center">
            <div className="text-2xl font-bold text-primary-600">
              {visibleApiSends.filter(s => s.status === 'pending').length}
            </div>
            <div className="text-sm text-primary-500">Pendentes</div>
          </div>
        </div>
        
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="text-center">
            <div className="text-2xl font-bold text-green-600">
              {visibleApiSends.filter(s => s.status === 'sent').length}
            </div>
            <div className="text-sm text-green-500">Enviados</div>
          </div>
        </div>
        
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="text-center">
            <div className="text-2xl font-bold text-red-600">
              {visibleApiSends.filter(s => s.status === 'failed').length}
            </div>
            <div className="text-sm text-red-500">Falharam</div>
          </div>
        </div>
        
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="text-center">
            <div className="text-2xl font-bold text-gray-600">
              {visibleApiSends.length}
            </div>
            <div className="text-sm text-gray-500">Total de Envios</div>
          </div>
        </div>
      </div>

      {/* Arquivos Processados para Integração */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h3 className="text-lg font-semibold text-gray-900 flex items-center">
              <Globe className="h-5 w-5 text-primary-600 mr-2" />
              Envios para a Integração
            </h3>
            <p className="text-sm text-gray-600 mt-1">Arquivos processados disponíveis para integração com APIs externas</p>
          </div>
          <button
            onClick={loadData}
            className="inline-flex items-center px-3 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700"
          >
            <RefreshCw className="h-4 w-4 mr-2" />
            Atualizar
          </button>
        </div>
        
        {loading ? (
          <div className="text-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto"></div>
            <p className="text-sm text-gray-500 mt-2">Carregando...</p>
          </div>
        ) : visibleProcessedFiles.length === 0 ? (
          <div className="text-center py-8">
            <FileText className="mx-auto h-12 w-12 text-gray-400 mb-4" />
            <p className="text-gray-500">Nenhum arquivo processado encontrado</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Arquivo
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Data de Processamento
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Tamanho
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Ações
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {groupByReferenceMonth(visibleProcessedFiles, (file) => file.original_name).map((group) => (
                  <React.Fragment key={group.key}>
                    <tr>
                      <td colSpan="5" className="px-6 py-2 bg-gray-100 text-sm font-semibold text-gray-800">
                        {group.label}
                      </td>
                    </tr>
                {group.items.map((file) => (
                  <tr key={file.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="flex-shrink-0 h-10 w-10">
                          <div className="h-10 w-10 rounded-lg bg-green-100 flex items-center justify-center">
                            <FileText className="h-5 w-5 text-green-600" />
                          </div>
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-medium text-gray-900">
                            {file.original_name}
                          </div>
                          <div className="text-sm text-gray-500">
                            Processado em {formatDate(file.updated_at)}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                        <CheckCircle className="h-3 w-3 mr-1" />
                        Processado
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {formatDateTime(file.updated_at)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {(file.file_size / 1024 / 1024).toFixed(2)} MB
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex space-x-2 justify-end">
                        <CasaFacilReportButtons fileId={file.id} fileName={file.original_name} />
                        <button
                          type="button"
                          onClick={() => openIntegrationModal(file)}
                          disabled={file.alreadyIntegrated}
                          title={file.alreadyIntegrated ? 'Arquivo já integrado. Use Reenviar na lista de Arquivos já Integrados.' : 'Integrar'}
                          className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 transition-colors disabled:cursor-not-allowed disabled:bg-gray-300 disabled:text-gray-500 disabled:hover:bg-gray-300"
                        >
                          <Zap className="h-4 w-4 mr-2" />
                          Integrar
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Arquivos Já Integrados */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h3 className="text-lg font-semibold text-gray-900 flex items-center">
              <CheckCircle className="h-5 w-5 text-green-600 mr-2" />
              Arquivos Já Integrados
            </h3>
            <p className="text-sm text-gray-600 mt-1">Arquivos que já foram enviados para integração com APIs externas</p>
          </div>
          <button
            onClick={loadData}
            className="inline-flex items-center px-3 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700"
          >
            <RefreshCw className="h-4 w-4 mr-2" />
            Atualizar
          </button>
        </div>
        
        {loading ? (
          <div className="text-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto"></div>
            <p className="text-sm text-gray-500 mt-2">Carregando...</p>
          </div>
        ) : (() => {
          const integratedFiles = visibleApiSends.filter(send => send.status === 'sent');
          return integratedFiles.length === 0 ? (
            <div className="text-center py-8">
              <CheckCircle className="mx-auto h-12 w-12 text-gray-400 mb-4" />
              <p className="text-gray-500">Nenhum arquivo integrado ainda</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Arquivo
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      API de Destino
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Data de Integração
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Ações
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {groupByReferenceMonth(integratedFiles, (apiSend) => apiSend.file_original_name).map((group) => (
                    <React.Fragment key={group.key}>
                      <tr>
                        <td colSpan="5" className="px-6 py-2 bg-gray-100 text-sm font-semibold text-gray-800">
                          {group.label}
                        </td>
                      </tr>
                  {group.items.map((apiSend) => (
                    <tr key={apiSend.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="flex-shrink-0 h-10 w-10">
                            <div className="h-10 w-10 rounded-lg bg-green-100 flex items-center justify-center">
                              <FileText className="h-5 w-5 text-green-600" />
                            </div>
                          </div>
                          <div className="ml-4">
                            <div className="text-sm font-medium text-gray-900">
                              {apiSend.file_original_name || 'N/A'}
                            </div>
                            <div className="text-sm text-gray-500">
                              ID: {apiSend.file_upload_id}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {apiSend.api_owner ? (
                          <div className="text-sm">
                            <div className="font-medium text-gray-900">{apiSend.api_owner}</div>
                            <div className="text-gray-500">{apiSend.api_function}</div>
                          </div>
                        ) : (
                          <span className="text-gray-400 text-sm">Não configurado</span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {formatDateTime(apiSend.sent_at)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                          <CheckCircle className="h-3 w-3 mr-1" />
                          Integrado
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex space-x-2 justify-end">
                          <button
                            onClick={() => {
                              const file = {
                                id: apiSend.file_upload_id,
                                original_name: apiSend.file_original_name,
                                file_size: apiSend.file_size || 0,
                                updated_at: apiSend.created_at
                              };
                              openIntegrationModal(file, true);
                            }}
                            className="inline-flex items-center px-3 py-1 border border-transparent text-xs font-medium rounded text-white bg-yellow-600 hover:bg-yellow-700"
                          >
                            <RefreshCw className="h-3 w-3 mr-1" />
                            Reenviar
                          </button>
                          <button
                            onClick={() => viewSendDetails(apiSend.id)}
                            className="inline-flex items-center px-3 py-1 border border-transparent text-xs font-medium rounded text-white bg-green-600 hover:bg-green-700"
                          >
                            <Eye className="h-3 w-3 mr-1" />
                            Ver Detalhes
                          </button>
                          <CasaFacilReportButtons fileId={apiSend.file_upload_id} fileName={apiSend.file_original_name} />
                        </div>
                      </td>
                    </tr>
                  ))}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          );
        })()}
      </div>

      {/* Lista de Envios API */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h3 className="text-lg font-semibold text-gray-900 flex items-center">
              <Send className="h-5 w-5 text-purple-600 mr-2" />
              Histórico de Envios API
            </h3>
            <p className="text-sm text-gray-600 mt-1">Acompanhe o histórico de todas as integrações realizadas</p>
          </div>
          <button
            onClick={loadData}
            className="inline-flex items-center px-3 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700"
          >
            <RefreshCw className="h-4 w-4 mr-2" />
            Atualizar
          </button>
        </div>
        
        {loading ? (
          <div className="text-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto"></div>
            <p className="text-sm text-gray-500 mt-2">Carregando...</p>
          </div>
        ) : visibleApiSends.length === 0 ? (
          <div className="text-center py-8">
            <FileText className="mx-auto h-12 w-12 text-gray-400 mb-4" />
            <p className="text-gray-500">Nenhum envio para integração encontrado</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Arquivo
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Configuração de API
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Data de Criação
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Data de Envio
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Ações
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {groupByReferenceMonth(visibleApiSends, (apiSend) => apiSend.file_original_name).map((group) => (
                  <React.Fragment key={group.key}>
                    <tr>
                      <td colSpan="6" className="px-6 py-2 bg-gray-100 text-sm font-semibold text-gray-800">
                        {group.label}
                      </td>
                    </tr>
                {group.items.map((apiSend) => (
                  <tr key={apiSend.id}>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <FileText className="h-5 w-5 text-gray-400 mr-3" />
                        <div>
                          <div className="text-sm font-medium text-gray-900">
                            {apiSend.file_original_name || 'N/A'}
                          </div>
                          <div className="text-sm text-gray-500">
                            ID: {apiSend.file_upload_id}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(apiSend.status)}`}>
                        {getStatusIcon(apiSend.status)}
                        <span className="ml-1 capitalize">{getStatusText(apiSend.status)}</span>
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {apiSend.api_owner ? (
                        <div className="text-sm">
                          <div className="font-medium text-gray-900">{apiSend.api_owner}</div>
                          <div className="text-gray-500">{apiSend.api_function}</div>
                        </div>
                      ) : (
                        <span className="text-gray-400 text-sm">Não configurado</span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {formatDateTime(apiSend.created_at)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {formatDateTime(apiSend.sent_at)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex space-x-2">
                        {apiSend.status === 'pending' && (
                          <button
                            onClick={() => sendToApi(apiSend.file_upload_id)}
                            className="inline-flex items-center px-3 py-1 border border-transparent text-xs font-medium rounded text-white bg-primary-600 hover:bg-primary-700"
                          >
                            <Send className="h-3 w-3 mr-1" />
                            Enviar
                          </button>
                        )}
                        
                        {apiSend.status === 'sent' && (
                          <button
                            onClick={() => {
                              // Buscar o arquivo original para atualização
                              const file = {
                                id: apiSend.file_upload_id,
                                original_name: apiSend.file_original_name,
                                file_size: apiSend.file_size || 0,
                                updated_at: apiSend.created_at
                              };
                              openIntegrationModal(file, true);
                            }}
                            className="inline-flex items-center px-3 py-1 border border-transparent text-xs font-medium rounded text-white bg-yellow-600 hover:bg-yellow-700"
                          >
                            <RefreshCw className="h-3 w-3 mr-1" />
                            Atualizar
                          </button>
                        )}
                        
                        {apiSend.status === 'failed' && (
                          <button
                            onClick={() => retrySend(apiSend.id)}
                            className="inline-flex items-center px-3 py-1 border border-transparent text-xs font-medium rounded text-white bg-orange-600 hover:bg-orange-700"
                          >
                            <Play className="h-3 w-3 mr-1" />
                            Tentar Novamente
                          </button>
                        )}
                        
                        <button
                          onClick={() => viewSendDetails(apiSend.id)}
                          className="inline-flex items-center px-3 py-1 border border-transparent text-xs font-medium rounded text-white bg-green-600 hover:bg-green-700"
                        >
                          <Eye className="h-3 w-3 mr-1" />
                          Ver Detalhes
                        </button>
                        <CasaFacilReportButtons fileId={apiSend.file_upload_id} fileName={apiSend.file_original_name} />
                      </div>
                    </td>
                  </tr>
                ))}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Modal de Integração */}
      {showIntegrationModal && selectedFile && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-11/12 max-w-2xl shadow-lg rounded-md bg-white">
            <div className="mt-3">
              {/* Header da Modal */}
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">
                    {resendMode ? '🔄 Atualização de Integração' : '🚀 Integração com API Externa'}
                  </h3>
                  <p className="text-sm text-gray-600 mt-1">
                    {resendMode ? 'Atualize a integração para o arquivo:' : 'Configure a integração para o arquivo:'} {selectedFile.original_name}
                  </p>
                </div>
                <button
                  onClick={closeIntegrationModal}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <X className="h-6 w-6" />
                </button>
              </div>

              {/* Seleção da Configuração de API */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Selecione a API de Destino *
                </label>
                <select
                  value={selectedApiConfig}
                  onChange={(e) => setSelectedApiConfig(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  required
                >
                  <option value="">Escolha uma configuração de API...</option>
                  {apiConfigs.map((config) => (
                    <option key={config.id} value={config.id}>
                      {config.api_owner} - {config.api_function}
                    </option>
                  ))}
                </select>
                {selectedApiConfig && (
                  <div className="mt-2 p-3 bg-primary-50 border border-primary-200 rounded-lg">
                    <div className="text-sm text-primary-800">
                      <div className="font-medium">Endpoint:</div>
                      <div className="font-mono text-xs break-all">
                        {apiConfigs.find(c => c.id == selectedApiConfig)?.endpoint}
                      </div>
                      <div className="mt-1">
                        <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-primary-100 text-primary-800 mr-2">
                          {apiConfigs.find(c => c.id == selectedApiConfig)?.method}
                        </span>
                        <span className="text-xs text-primary-600">
                          {apiConfigs.find(c => c.id == selectedApiConfig)?.description}
                        </span>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Informações do Arquivo */}
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 mb-6">
                <h4 className="text-sm font-semibold text-gray-800 mb-2">📁 Informações do Arquivo</h4>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="font-medium text-gray-700">Nome:</span>
                    <p className="text-gray-600">{selectedFile.original_name}</p>
                  </div>
                  <div>
                    <span className="font-medium text-gray-700">Tamanho:</span>
                    <p className="text-gray-600">{(selectedFile.file_size / 1024 / 1024).toFixed(2)} MB</p>
                  </div>
                  <div>
                    <span className="font-medium text-gray-700">Status:</span>
                    <p className="text-gray-600">Processado</p>
                  </div>
                  <div>
                    <span className="font-medium text-gray-700">Processado em:</span>
                    <p className="text-gray-600">{formatDateTime(selectedFile.updated_at)}</p>
                  </div>
                </div>
              </div>

              {/* Botões de Ação */}
              <div className="flex gap-3 justify-end">
                <button
                  onClick={closeIntegrationModal}
                  className="px-4 py-2 border border-gray-300 text-gray-700 text-sm font-medium rounded-md hover:bg-gray-50 transition-colors"
                >
                  Cancelar
                </button>
                
                {/* Botão Visualizar JSON */}
                <button
                  onClick={handlePreviewJson}
                  disabled={!selectedApiConfig || integrationLoading}
                  title="Visualizar o JSON que será enviado para a API (para debug)"
                  className={`px-4 py-2 border border-gray-300 text-sm font-medium rounded-md transition-colors ${
                    !selectedApiConfig || integrationLoading
                      ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                      : 'bg-green-50 text-green-700 hover:bg-green-100 border-green-300'
                  }`}
                >
                  {integrationLoading ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-green-600 inline-block mr-2"></div>
                      Gerando...
                    </>
                  ) : (
                    <>
                      <Eye className="h-4 w-4 inline-block mr-2" />
                      Visualizar JSON
                    </>
                    )}
                </button>
                
                <button
                  onClick={handleIntegration}
                  disabled={!selectedApiConfig || integrationLoading}
                  className={`px-4 py-2 text-white text-sm font-medium rounded-md transition-colors ${
                    !selectedApiConfig || integrationLoading
                      ? 'bg-gray-400 cursor-not-allowed'
                      : 'bg-primary-600 hover:bg-primary-700'
                  }`}
                >
                  {integrationLoading ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white inline-block mr-2"></div>
                      {resendMode ? 'Atualizando...' : 'Integrando...'}
                    </>
                  ) : (
                    <>
                      <Zap className="h-4 w-4 inline-block mr-2" />
                      {resendMode ? 'Atualizar Integração' : 'Iniciar Integração'}
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Preview do JSON */}
      {showJsonPreview && jsonPreviewData && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-11/12 max-w-6xl shadow-lg rounded-md bg-white">
            <div className="mt-3">
              {/* Header da Modal */}
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">
                    📋 Preview do JSON para Integração
                  </h3>
                  <p className="text-sm text-gray-600 mt-1">
                    Visualize o payload que será enviado para a API antes da integração
                  </p>
                </div>
                <button
                  onClick={closeJsonPreview}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <X className="h-6 w-6" />
                </button>
              </div>

              {/* Informações da Configuração */}
              <div className="bg-primary-50 border border-primary-200 rounded-lg p-4 mb-6">
                <h4 className="text-sm font-semibold text-primary-800 mb-2">⚙️ Configuração da API</h4>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                  <div>
                    <span className="font-medium text-primary-700">Proprietário:</span>
                    <p className="text-primary-600">{jsonPreviewData.api_config.owner}</p>
                  </div>
                  <div>
                    <span className="font-medium text-primary-700">Função:</span>
                    <p className="text-primary-600">{jsonPreviewData.api_config.function}</p>
                  </div>
                  <div>
                    <span className="font-medium text-primary-700">Método:</span>
                    <p className="text-primary-600">{jsonPreviewData.method}</p>
                  </div>
                  <div>
                    <span className="font-medium text-primary-700">Total de Registros:</span>
                    <p className="text-primary-600">{jsonPreviewData.total_records}</p>
                  </div>
                </div>
                <div className="mt-3">
                  <span className="font-medium text-primary-700">Endpoint:</span>
                  <p className="text-primary-600 font-mono text-xs break-all">{jsonPreviewData.endpoint}</p>
                </div>
              </div>

              {/* Headers */}
              <div className="mb-6">
                <h4 className="text-md font-semibold text-gray-900 mb-4">
                  🔐 Headers da Requisição
                </h4>
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                  <pre className="text-sm text-gray-800 whitespace-pre-wrap overflow-x-auto">
                    {JSON.stringify(jsonPreviewData.headers, null, 2)}
                  </pre>
                </div>
              </div>

              {/* Dados do Arquivo (Preview) */}
              <div className="mb-6">
                <h4 className="text-md font-semibold text-gray-900 mb-4">
                  📊 Dados do Arquivo (Preview - 3 primeiros registros formatados)
                </h4>
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                  {jsonPreviewData.preview_data ? (
                    jsonPreviewData.preview_data.map((record, index) => (
                      <div key={index} className="mb-4 p-3 bg-white rounded border">
                        <h5 className="font-semibold text-sm mb-2">Registro {record.record_number}</h5>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <h6 className="font-medium text-xs text-gray-700 mb-1">Dados Originais:</h6>
                            <pre className="text-xs bg-gray-100 p-2 rounded overflow-auto max-h-32">
                              {JSON.stringify(record.original_data, null, 2)}
                            </pre>
                          </div>
                          <div>
                            <h6 className="font-medium text-xs text-gray-700 mb-1">Payload Formatado MAXPAR:</h6>
                            <pre className="text-xs bg-primary-50 p-2 rounded overflow-auto max-h-32">
                              {JSON.stringify(record.formatted_payload, null, 2)}
                            </pre>
                          </div>
                        </div>
                      </div>
                    ))
                  ) : (
                    <pre className="text-sm text-gray-800 whitespace-pre-wrap overflow-x-auto">
                      {JSON.stringify(jsonPreviewData.data, null, 2)}
                    </pre>
                  )}
                  <div className="mt-3 text-sm text-gray-600">
                    <span className="font-medium">Nota:</span> Mostrando apenas os 3 primeiros registros de {jsonPreviewData.total_records} total.
                  </div>
                </div>
              </div>

              {/* Payload Completo */}
              <div className="mb-6">
                <h4 className="text-md font-semibold text-gray-900 mb-4">
                  🚀 Payload Completo para Envio (Formatado MAXPAR)
                </h4>
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                  <div className="mb-4">
                    <h5 className="font-medium text-sm text-yellow-800 mb-2">📡 Configuração da API:</h5>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                      <div>
                        <span className="font-medium">Endpoint:</span> {jsonPreviewData.endpoint}
                      </div>
                      <div>
                        <span className="font-medium">Método:</span> {jsonPreviewData.method}
                      </div>
                      <div>
                        <span className="font-medium">Proprietário:</span> {jsonPreviewData.api_config?.owner}
                      </div>
                      <div>
                        <span className="font-medium">Função:</span> {jsonPreviewData.api_config?.function}
                      </div>
                    </div>
                  </div>
                  
                  <div className="mb-4">
                    <h5 className="font-medium text-sm text-yellow-800 mb-2">📋 Headers da Requisição:</h5>
                    <pre className="text-xs bg-white p-2 rounded border overflow-auto max-h-32">
                      {JSON.stringify(jsonPreviewData.headers, null, 2)}
                    </pre>
                  </div>
                  
                  <div>
                    <h5 className="font-medium text-sm text-yellow-800 mb-2">📊 Dados Formatados para Envio (MAXPAR):</h5>
                    {jsonPreviewData.preview_data ? (
                      jsonPreviewData.preview_data.map((record, index) => (
                        <div key={index} className="mb-3 p-3 bg-white rounded border">
                          <h6 className="font-medium text-sm mb-2">Registro {record.record_number}:</h6>
                          <pre className="text-xs bg-primary-50 p-2 rounded border overflow-auto max-h-40">
                            {JSON.stringify(record.formatted_payload, null, 2)}
                          </pre>
                        </div>
                      ))
                    ) : (
                      <pre className="text-xs bg-primary-50 p-2 rounded border overflow-auto max-h-40">
                        {JSON.stringify(jsonPreviewData.data, null, 2)}
                      </pre>
                    )}
                  </div>
                  
                  <div className="mt-3 text-sm text-yellow-700">
                    <span className="font-medium">ℹ️ Nota:</span> Este é o payload exato que será enviado para a API MAXPAR.
                  </div>
                </div>
              </div>

              {/* Botões de Ação */}
              <div className="flex gap-3 justify-end">
                <button
                  onClick={closeJsonPreview}
                  className="px-4 py-2 border border-gray-300 text-gray-700 text-sm font-medium rounded-md hover:bg-gray-50 transition-colors"
                >
                  Fechar Preview
                </button>
                <button
                  onClick={handleIntegration}
                  disabled={integrationLoading}
                  className={`px-4 py-2 text-white text-sm font-medium rounded-md transition-colors ${
                    integrationLoading
                      ? 'bg-gray-400 cursor-not-allowed'
                      : 'bg-primary-600 hover:bg-primary-700'
                  }`}
                >
                  {integrationLoading ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white inline-block mr-2"></div>
                      Iniciar Integração
                    </>
                  ) : (
                    <>
                      <Zap className="h-4 w-4 inline-block mr-2" />
                      Iniciar Integração
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {viewingData && (
        <SendDetailsPanel
          send={selectedSendData}
          loading={detailsLoading}
          error={detailsError}
          onClose={closeDataView}
          getStatusText={getStatusText}
          formatDateTime={formatDateTime}
        />
      )}
    </Layout>
  );
}

export default ApiSends;
